import SkeletonRow from "@/components/SkeletonRow";
import { Checkbox } from "@/components/ui/checkbox";
import { useDataValidasi } from "@/store/dataValidasiStore";
import { UserCheck } from "lucide-react";

export default function DataBiodata({
  isPending,
  isValidPermanen,
}: {
  isPending: boolean;
  isValidPermanen: boolean;
}) {
  const { dataBiodata, setDataBiodata } = useDataValidasi();

  const handleValidasi = (label: string) => {
    const updatedBiodata = dataBiodata.map((item) =>
      item.label === label ? { ...item, isValidated: !item.isValidated } : item,
    );

    setDataBiodata(updatedBiodata);

    const dataValidasiString = localStorage.getItem("dataValidasi");
    const previousData = dataValidasiString
      ? JSON.parse(dataValidasiString)
      : {};
    const previousKeluarga = previousData.keluarga || [];
    const previousKeluargaTambahan = previousData.keluarga_tambahan || null;
    const previousAset = previousData.aset || [];
    const previousBerkas = previousData.berkas || [];

    const updatedDataValidasi = {
      biodata: updatedBiodata,
      keluarga: previousKeluarga,
      keluarga_tambahan: previousKeluargaTambahan,
      aset: previousAset,
      berkas: previousBerkas,
    };

    localStorage.setItem("dataValidasi", JSON.stringify(updatedDataValidasi));
  };

  if (isPending)
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => (
            <SkeletonRow key={index} />
          ))}
      </div>
    );

  return (
    <div className="flex flex-col gap-4 w-full p-2 sm:p-5">
      {/* HEADER SECTION (Opsional, agar menyatu dengan desain sebelumnya) */}
      <div className="flex items-center gap-2 px-1">
        <UserCheck className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">
          Verifikasi Biodata Diri
        </h2>
      </div>

      {/* LIST CONTAINER */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
          {dataBiodata?.map((item, index) => {
            // Tentukan status checked agar kode lebih bersih
            const isChecked = isValidPermanen || item.isValidated;

            // Logic value display
            const displayValue =
              item?.label === "Kebutuhan Khusus" && !item.value
                ? "Tidak Ada"
                : item.value || "-";

            return (
              <label
                key={index}
                // Jika tidak permanen, hover effect jalan. Jika checked, background hijau lembut.
                className={`flex items-center justify-between p-4 sm:px-6 transition-all duration-200 ${
                  !isValidPermanen ? "cursor-pointer group" : "cursor-default"
                } ${
                  isChecked
                    ? "bg-emerald-50/50 dark:bg-emerald-950/10"
                    : "hover:bg-slate-50 dark:hover:bg-slate-800/50 bg-white dark:bg-slate-900"
                }`}
              >
                {/* DATA KIRI: Label & Value */}
                <div className="flex flex-col gap-1.5 pr-4">
                  <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {item.label}
                  </span>
                  <span
                    className={`text-sm sm:text-base font-medium transition-colors ${
                      isChecked
                        ? "text-slate-900 dark:text-slate-100"
                        : "text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {displayValue}
                  </span>
                </div>

                {/* AKSI KANAN: Checkbox & Helper Text */}
                <div className="flex items-center gap-4 shrink-0">
                  {/* Helper text (Sembunyi di mobile, muncul di desktop saat di-hover/checked) */}
                  <span
                    className={`text-xs font-bold uppercase tracking-wider hidden sm:block transition-all duration-300 ${
                      isChecked
                        ? "text-emerald-600 dark:text-emerald-500 opacity-100 translate-x-0"
                        : "text-slate-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0"
                    }`}
                  >
                    {isChecked ? "Valid" : "Tandai Valid"}
                  </span>

                  <Checkbox
                    className={`h-6 w-6 rounded-md transition-all shadow-sm ${
                      isChecked
                        ? "data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                        : "border-slate-300 dark:border-slate-600 group-hover:border-primary/50"
                    }`}
                    checked={isChecked}
                    onCheckedChange={() =>
                      !isValidPermanen && handleValidasi(item.label)
                    }
                    disabled={isValidPermanen} // Matikan klik langsung dari checkbox jika sudah permanen
                  />
                </div>
              </label>
            );
          })}

          {/* Fallback Kosong */}
          {(!dataBiodata || dataBiodata.length === 0) && (
            <div className="p-8 text-center text-slate-500">
              Data biodata tidak ditemukan.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
